import type { DemoUser, UserRole, DemoStaffDetails, DemoVisionaryDetails } from '../types/demo';

// Mock permissions based on your existing role system
const ADMIN_PERMISSIONS = {
  can_manage_staff: true,
  can_amend_prices: true,
  can_view_reports: true,
  can_manage_inventory: true,
  can_manage_warehouse: true,
  can_manage_customers: true
};

const MANAGER_PERMISSIONS = {
  can_amend_prices: true,
  can_view_reports: true,
  can_manage_inventory: true,
  can_manage_customers: true
};

const SALES_PERMISSIONS = {
  can_process_orders: true,
  can_lookup_customers: true,
  can_view_products: true
};

const WAREHOUSE_PERMISSIONS = {
  can_manage_orders: true,
  can_update_stock: true,
  can_process_returns: true
};

const VISIONARY_PERMISSIONS = {
  can_shop: true,
  can_view_orders: true,
  can_manage_account: true
};

export const DEMO_USERS: Record<UserRole, DemoUser> = {
  admin: {
    id: 'demo-admin',
    role: 'admin',
    username: 'demo.admin',
    full_name: 'Demo Administrator',
    staff_details: JSON.stringify({
      staff_id: 'demo-admin-staff',
      role: 'admin',
      full_name: 'Demo Administrator'
    }),
    permissions: ADMIN_PERMISSIONS
  },
  store_manager: {
    id: 'demo-manager',
    role: 'store_manager',
    username: 'demo.manager',
    full_name: 'Demo Store Manager',
    staff_details: JSON.stringify({
      staff_id: 'demo-manager-staff',
      role: 'store_manager',
      full_name: 'Demo Store Manager'
    }),
    permissions: MANAGER_PERMISSIONS
  },
  assistant_manager: {
    id: 'demo-assistant-manager',
    role: 'assistant_manager',
    username: 'demo.assistant',
    full_name: 'Demo Assistant Manager',
    staff_details: JSON.stringify({
      staff_id: 'demo-assistant-staff',
      role: 'assistant_manager',
      full_name: 'Demo Assistant Manager'
    }),
    permissions: { ...SALES_PERMISSIONS, can_manage_inventory: true }
  },
  sales_assistant: {
    id: 'demo-sales',
    role: 'sales_assistant',
    username: 'demo.sales',
    full_name: 'Demo Sales Assistant',
    staff_details: JSON.stringify({
      staff_id: 'demo-sales-staff',
      role: 'sales_assistant',
      full_name: 'Demo Sales Assistant'
    }),
    permissions: SALES_PERMISSIONS
  },
  invoices_clerk: {
    id: 'demo-invoices',
    role: 'invoices_clerk',
    username: 'demo.invoices',
    full_name: 'Demo Invoices Clerk',
    staff_details: JSON.stringify({
      staff_id: 'demo-invoices-staff',
      role: 'invoices_clerk',
      full_name: 'Demo Invoices Clerk'
    }),
    permissions: { ...SALES_PERMISSIONS, can_view_reports: true }
  },
  stock_control_manager: {
    id: 'demo-stock',
    role: 'stock_control_manager',
    username: 'demo.stock',
    full_name: 'Demo Stock Control Manager',
    staff_details: JSON.stringify({
      staff_id: 'demo-stock-staff',
      role: 'stock_control_manager',
      full_name: 'Demo Stock Control Manager'
    }),
    permissions: { ...SALES_PERMISSIONS, can_manage_inventory: true }
  },
  warehouse_assistant: {
    id: 'demo-warehouse',
    role: 'warehouse_assistant',
    username: 'demo.warehouse',
    full_name: 'Demo Warehouse Assistant',
    staff_details: JSON.stringify({
      staff_id: 'demo-warehouse-staff',
      role: 'warehouse_assistant',
      full_name: 'Demo Warehouse Assistant'
    }),
    permissions: WAREHOUSE_PERMISSIONS
  },
  visionary: {
    id: 'demo-visionary',
    role: 'visionary',
    username: 'demo.customer',
    full_name: 'Demo Customer',
    visionary_details: JSON.stringify({
      membership_number: 999999,
      name: 'Demo Customer',
      email: 'demo@bigvisiongames.com',
      loyalty_points: 1250,
      address: '123 Demo Street, Demo City, DC1 2EM',
      telephone: '01234567890'
    } as DemoVisionaryDetails),
    permissions: VISIONARY_PERMISSIONS
  }
};

export const getRoleDisplayName = (role: UserRole): string => {
  const roleMap: Record<UserRole, string> = {
    'admin': 'Administrator',
    'sales_assistant': 'Sales Assistant',
    'invoices_clerk': 'Invoices Clerk',
    'store_manager': 'Store Manager',
    'assistant_manager': 'Assistant Manager',
    'stock_control_manager': 'Stock Control Manager',
    'warehouse_assistant': 'Warehouse Assistant',
    'visionary': 'Customer (Visionary)'
  };
  return roleMap[role] || role.charAt(0).toUpperCase() + role.slice(1);
};

export const getRoleDescription = (role: UserRole): string => {
  const descriptions: Record<UserRole, string> = {
    'admin': 'Full system access - manage all users, settings, and operations',
    'store_manager': 'Manage store operations, staff, inventory, and pricing',
    'assistant_manager': 'Support store operations and inventory management',
    'sales_assistant': 'Process customer orders and handle basic customer service',
    'invoices_clerk': 'Handle billing, invoices, and payment processing',
    'stock_control_manager': 'Manage inventory levels and stock control',
    'warehouse_assistant': 'Handle order fulfillment, shipping, and returns',
    'visionary': 'Shop for products, manage orders and loyalty points'
  };
  return descriptions[role] || '';
};

// Demo products data
export const DEMO_PRODUCTS = [
  {
    id: 'demo-product-1',
    name: 'The Legend of Zelda: Tears of the Kingdom',
    category: 'Games',
    platform: 'Nintendo Switch',
    price: 59.99,
    stock_quantity: 25,
    image_url: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=300',
    description: 'An epic adventure awaits in the latest Zelda adventure.',
    status: 'active'
  },
  {
    id: 'demo-product-2',
    name: 'PlayStation 5 Console',
    category: 'Consoles',
    platform: 'PlayStation 5',
    price: 479.99,
    stock_quantity: 3,
    image_url: 'https://images.unsplash.com/photo-1606813907291-d86efa9b94db?w=300',
    description: 'Next-generation gaming console with ultra-fast SSD.',
    status: 'active'
  },
  {
    id: 'demo-product-3',
    name: 'Spider-Man 2',
    category: 'Games',
    platform: 'PlayStation 5',
    price: 69.99,
    stock_quantity: 15,
    image_url: 'https://images.unsplash.com/photo-1635805737707-575885ab0820?w=300',
    description: 'Swing through New York as Spider-Man in this epic sequel.',
    status: 'active'
  },
  {
    id: 'demo-product-4',
    name: 'Xbox Series X Console',
    category: 'Consoles',
    platform: 'Xbox Series X/S',
    price: 449.99,
    stock_quantity: 5,
    image_url: 'https://images.unsplash.com/photo-1621259182978-fbf93132d53d?w=300',
    description: 'Powerful gaming console with 4K gaming capability.',
    status: 'active'
  },
  {
    id: 'demo-product-5',
    name: 'Forza Horizon 5',
    category: 'Games',
    platform: 'Xbox Series X/S',
    price: 54.99,
    stock_quantity: 20,
    image_url: 'https://images.unsplash.com/photo-1511512578047-dfb367046420?w=300',
    description: 'Open-world racing adventure set in Mexico.',
    status: 'active'
  },
  {
    id: 'demo-product-6',
    name: 'Nintendo Switch OLED',
    category: 'Consoles',
    platform: 'Nintendo Switch',
    price: 309.99,
    stock_quantity: 8,
    image_url: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=300',
    description: 'Enhanced Switch with beautiful OLED display.',
    status: 'active'
  },
  {
    id: 'demo-product-7',
    name: 'DualSense Wireless Controller',
    category: 'Accessories',
    platform: 'PlayStation 5',
    price: 64.99,
    stock_quantity: 12,
    image_url: 'https://images.unsplash.com/photo-1606813907291-d86efa9b94db?w=300',
    description: 'Revolutionary controller with haptic feedback.',
    status: 'active'
  },
  {
    id: 'demo-product-8',
    name: 'Gaming Headset Pro',
    category: 'Accessories',
    platform: 'PC',
    price: 89.99,
    stock_quantity: 18,
    image_url: 'https://images.unsplash.com/photo-1599669454699-248893623440?w=300',
    description: 'Professional gaming headset with 7.1 surround sound.',
    status: 'active'
  }
];

// Demo orders data for visionaries
export const DEMO_ORDERS = [
  {
    id: 'demo-order-1',
    invoice_id: 'BVG-2024-001',
    visionary_id: 999999,
    total_amount: 129.98,
    points_earned: 130,
    points_used: 0,
    status: 'completed',
    created_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days ago
    order_items: [
      {
        id: 'demo-item-1',
        product_id: 'demo-product-1',
        product_name: 'The Legend of Zelda: Tears of the Kingdom',
        quantity: 1,
        price_at_time: 59.99
      },
      {
        id: 'demo-item-2',
        product_id: 'demo-product-7',
        product_name: 'DualSense Wireless Controller',
        quantity: 1,
        price_at_time: 69.99
      }
    ]
  },
  {
    id: 'demo-order-2',
    invoice_id: 'BVG-2024-002',
    visionary_id: 999999,
    total_amount: 54.99,
    points_earned: 55,
    points_used: 0,
    status: 'processing',
    created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
    order_items: [
      {
        id: 'demo-item-3',
        product_id: 'demo-product-5',
        product_name: 'Forza Horizon 5',
        quantity: 1,
        price_at_time: 54.99
      }
    ]
  },
  {
    id: 'demo-order-3',
    invoice_id: 'BVG-2024-003',
    visionary_id: 999999,
    total_amount: 89.99,
    points_earned: 90,
    points_used: 100,
    status: 'completed',
    created_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(), // 14 days ago
    order_items: [
      {
        id: 'demo-item-4',
        product_id: 'demo-product-8',
        product_name: 'Gaming Headset Pro',
        quantity: 1,
        price_at_time: 89.99
      }
    ]
  }
];

export const DEMO_SUCCESS_RESPONSE = {
  data: { success: true },
  error: null,
  status: 200,
  statusText: 'OK'
};

export const WRITE_OPERATIONS = [
  'insert',
  'update',
  'delete',
  'upsert',
  'create_login',
  'update_staff_password',
  'update_visionary_details',
  'update_visionary_points',
  'anonymize_visionary_account',
  'direct_update_staff_name',
  'direct_update_login_role'
]; 